/**
 * Consent Narrative Helpers
 * 
 * This module provides utilities for fetching consent narrative text from the backend.
 * The backend is the SINGLE SOURCE OF TRUTH for consent language.
 * 
 * Version tracking is included to support historical consent text for signed documents.
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetch consent narrative from the backend API
 * 
 * @param {Object} consentData - The consent data object with pos_code, modality, provider_location, etc.
 * @param {string} token - Auth token for API call
 * @returns {Promise<Object>} - The consent narrative response with text, html, modality, version, etc.
 */
export async function fetchConsentNarrative(consentData, token) {
  if (!consentData || typeof consentData !== 'object') {
    return {
      text: '',
      html: '',
      modality: null,
      version: '',
      updated_at: '',
      is_valid: false,
      provider_location_text: ''
    };
  }

  // Skip API call if no meaningful consent data
  if (!consentData.pos_code && !consentData.modality) {
    return {
      text: '',
      html: '',
      modality: null,
      version: '',
      updated_at: '',
      is_valid: false,
      provider_location_text: ''
    };
  }

  try {
    const response = await axios.post(
      `${API}/api/utils/consent-narrative`,
      { consent_data: consentData },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching consent narrative:', error);
    // Return empty result on error
    return {
      text: '',
      html: '',
      modality: null,
      version: '',
      updated_at: '',
      is_valid: false,
      provider_location_text: ''
    };
  }
}

/**
 * Get modality display info (icon name, colors) for consent display
 * 
 * @param {string} modality - The modality type: 'in_person', 'audio_visual', or 'audio_only'
 * @returns {Object} - Display info with iconType, bgColor, borderColor, textColor
 */
export function getModalityDisplayInfo(modality) {
  switch (modality) {
    case 'in_person':
      return {
        iconType: 'users',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconColor: 'text-green-600'
      };
    case 'audio_visual':
      return {
        iconType: 'monitor',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconColor: 'text-blue-600'
      };
    case 'audio_only':
      return {
        iconType: 'phone',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        iconColor: 'text-purple-600'
      };
    default:
      return {
        iconType: 'building',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        iconColor: 'text-gray-600'
      };
  }
}

/**
 * Cache for consent narratives to avoid redundant API calls
 * Key: JSON string of consent data
 * Value: { result, timestamp }
 */
const consentCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch consent narrative with caching
 * 
 * @param {Object} consentData - The consent data object
 * @param {string} token - Auth token for API call
 * @returns {Promise<Object>} - The consent narrative response
 */
export async function fetchConsentNarrativeCached(consentData, token) {
  const cacheKey = JSON.stringify(consentData);
  const cached = consentCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
    return cached.result;
  }
  
  const result = await fetchConsentNarrative(consentData, token);
  consentCache.set(cacheKey, { result, timestamp: Date.now() });
  
  return result;
}

/**
 * Clear the consent narrative cache
 */
export function clearConsentCache() {
  consentCache.clear();
}
